/**
 * Spreadsheet export for Tableau Reports tabs (aggregated pivots, not raw
 * cache records). Reuses triggerDownload from exportData.js.
 *
 * Important: User Logins / Spoke Totals store `buckets` as arrays indexed by
 * column position. Drilldown 2.0 stores `buckets` as objects keyed by
 * column.key. Both shapes are supported.
 */

import * as XLSX from 'xlsx';
import { triggerDownload, buildFilenameStem } from './exportData';

/**
 * Download an array of plain objects as a single-sheet .xlsx.
 * Always pass `headers` when column order matters — Object key order is
 * unreliable for integer-like header names (e.g. week day "12").
 */
export function downloadObjectsAsXlsx(rows, {
  fileStem,
  sheetName = 'Report',
  headers,
  startDate,
  endDate,
} = {}) {
  const list = Array.isArray(rows) ? rows : [];
  const cols = Array.isArray(headers) && headers.length
    ? headers
    : (list.length
      ? Object.keys(list.reduce((acc, r) => {
        Object.keys(r || {}).forEach(k => { acc[k] = true; });
        return acc;
      }, {}))
      : []);

  const objects = list.length
    ? list.map(r => {
      const o = {};
      cols.forEach(h => { o[h] = r?.[h] ?? ''; });
      return o;
    })
    : [{}];

  // aoa_to_sheet preserves exact column order (json_to_sheet + integer-like
  // keys reorders columns in Excel).
  const aoa = [
    cols,
    ...objects.map(o => cols.map(h => o[h])),
  ];
  const sheet = XLSX.utils.aoa_to_sheet(aoa);
  const wb = XLSX.utils.book_new();
  const safeName = String(sheetName || 'Report').slice(0, 31) || 'Report';
  XLSX.utils.book_append_sheet(wb, sheet, safeName);

  const arrayBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([arrayBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const stem = buildFilenameStem(fileStem || 'tableau-report', startDate, endDate);
  triggerDownload(blob, `${stem}.xlsx`);
}

/** Readable period column title for export (not just "12" for a week leaf). */
export function periodColumnHeader(c) {
  if (!c) return '';
  if (c.label && !/^\d{1,2}$/.test(String(c.label).trim())) return c.label;
  const key = String(c.key || '');
  if (/^\d{4}-\d{2}-\d{2}$/.test(key)) {
    const [y, m, d] = key.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    if (!Number.isNaN(dt.getTime())) {
      return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
  }
  if (c.monthLabel && c.leafLabel && /^\d{1,2}$/.test(String(c.leafLabel).trim())) {
    // e.g. monthLabel "July 2026", leaf "12" → "Jul 12, 2026"
    const monthPart = String(c.monthLabel).split(/\s+/)[0];
    const yearPart = (String(c.monthLabel).match(/\d{4}/) || [])[0] || '';
    return yearPart ? `${monthPart} ${c.leafLabel}, ${yearPart}` : `${monthPart} ${c.leafLabel}`;
  }
  return c.leafLabel || c.key || '';
}

function bucketValue(row, col, index) {
  const b = row?.buckets;
  if (Array.isArray(b)) return Number(b[index]) || 0;
  if (b && typeof b === 'object') return Number(b[col.key]) || 0;
  return 0;
}

/**
 * Flatten a heatmap aggregate ({ columns, rows }) into export objects +
 * ordered headers. Supports array or object `buckets`.
 *
 * @returns {{ rows: object[], headers: string[] }}
 */
export function heatmapToObjects(result, {
  idColumns = [],
  includeTotal = true,
} = {}) {
  const columns = result?.columns || [];
  const sourceRows = result?.rows || [];

  const headers = [];
  const idSpecs = idColumns.map(col => (
    typeof col === 'string'
      ? { key: col, header: col }
      : { key: col.key, header: col.header || col.key }
  ));
  for (const spec of idSpecs) headers.push(spec.header);
  for (const c of columns) headers.push(periodColumnHeader(c));
  if (includeTotal) headers.push('Grand total');

  // Disambiguate duplicate period headers (rare but breaks object keys).
  const seen = new Map();
  const uniqueHeaders = headers.map((h) => {
    const n = (seen.get(h) || 0) + 1;
    seen.set(h, n);
    return n === 1 ? h : `${h} (${n})`;
  });

  const rows = sourceRows.map((r) => {
    const o = {};
    let hi = 0;
    for (const spec of idSpecs) {
      let val = r?.[spec.key] ?? '';
      if (spec.key === 'entityLabel' && !val) val = r?.rowLabel ?? '';
      o[uniqueHeaders[hi++]] = val;
    }
    columns.forEach((c, i) => {
      o[uniqueHeaders[hi++]] = bucketValue(r, c, i);
    });
    if (includeTotal) {
      o[uniqueHeaders[hi]] = r?.total ?? 0;
    }
    return o;
  });

  return { rows, headers: uniqueHeaders };
}

/**
 * Flatten Drilldown spoke result: one row per sending user under each
 * sending facility, plus a facility-total row.
 */
export function spokeResultToObjects(spokeResult, hubContext = {}) {
  const out = [];
  const hubs = hubContext.facilityLabel || '';
  const hubUser = hubContext.userLabel || '';
  for (const fac of spokeResult?.rows || []) {
    const sendingFacility = fac.rowLabel || fac.facilityName || fac.name || '';
    out.push({
      'Hub facility': hubs,
      'Hub user': hubUser,
      'Sending facility': sendingFacility,
      'Sending user': '(facility total)',
      Referrals: fac.total ?? fac.referrals ?? 0,
    });
    for (const u of fac.users || []) {
      out.push({
        'Hub facility': hubs,
        'Hub user': hubUser,
        'Sending facility': sendingFacility,
        'Sending user': u.rowLabel || u.userName || u.name || '',
        Referrals: u.total ?? u.referrals ?? 0,
      });
    }
  }
  return out;
}
